import type { ReactElement } from 'react';
import { Alert, Badge, Button, Checkbox, Group, Modal, Paper, ScrollArea, Select, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { Sparkles } from 'lucide-react';
import type { ClassificationSuggestion, ClassificationSuggestionFields } from '@/shared/assist/ClassificationAssistContract';
import type { ClassificationPageViewModel } from './ClassificationPageViewModel';
import type { ClassificationOutputType } from './ClassificationService';
import { OUTPUT_TYPES, describeOutputConfig, type CategoryNoun } from './ClassificationOptions';

interface ClassificationAssistModalProps {
    vm: ClassificationPageViewModel;
    noun: CategoryNoun;
}

/**
 * Review surface for the classification / policy AI assist: the assistant's proposed
 * add/update suggestions are shown as selectable cards whose fields can be edited
 * inline. Only the selected suggestions are applied; each is created or updated on the
 * server when you click Apply. The prompt itself is entered in the inline composer that
 * opens this modal.
 */
export default function ClassificationAssistModal({ vm, noun }: ClassificationAssistModalProps): ReactElement {
    const hasSuggestions = vm.suggestions.length > 0;
    const selectedCount = vm.selectedSuggestionIndices.size;
    const allSelected = hasSuggestions && selectedCount === vm.suggestions.length;
    const someSelected = selectedCount > 0 && !allSelected;
    const applying = vm.saveStatus === 'saving';

    return (
        <Modal opened={vm.assistModalOpen} onClose={() => vm.closeAssist()} title={`AI assist — ${noun.plural}`} size="xl" centered>
            <Stack gap="md">
                {vm.assistNarrative.length > 0 && <Text size="sm">{vm.assistNarrative}</Text>}

                {vm.assistError !== null && (
                    <Alert color="red" variant="light">
                        {vm.assistError}
                    </Alert>
                )}

                {!hasSuggestions && (
                    <Alert color="blue" variant="light" title="No changes to apply">
                        <Text size="sm" c="dimmed">
                            Try being more specific about what you want — for example “fill in any policies that are missing”, “make the
                            confidential-data policy stricter”, or “add a policy for off-policy tool use”.
                        </Text>
                    </Alert>
                )}

                {hasSuggestions && (
                    <Stack gap="sm">
                        <Group justify="space-between">
                            <Checkbox
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={(event) => vm.setAllSuggestionsSelected(event.currentTarget.checked)}
                                label="Select all"
                            />
                            <Text size="xs" c="dimmed">
                                {selectedCount} of {vm.suggestions.length} selected
                            </Text>
                        </Group>
                        <ScrollArea.Autosize mah={420} type="auto" offsetScrollbars="y" pr={4} pb={2}>
                            <Stack gap="xs" my="xs">
                                {vm.suggestions.map((suggestion, index) => (
                                    <SuggestionCard key={index} vm={vm} index={index} suggestion={suggestion} noun={noun} />
                                ))}
                            </Stack>
                        </ScrollArea.Autosize>
                    </Stack>
                )}

                <Group justify="flex-end" gap="sm" mt="sm">
                    <Button variant="default" onClick={() => vm.closeAssist()}>
                        Cancel
                    </Button>
                    {hasSuggestions && (
                        <Button leftSection={<Sparkles size={15} />} loading={applying} disabled={!vm.canApplySuggestions} onClick={() => void vm.applySelectedSuggestions()}>
                            Apply{selectedCount > 0 ? ` (${selectedCount})` : ''}
                        </Button>
                    )}
                </Group>
            </Stack>
        </Modal>
    );
}

interface SuggestionCardProps {
    vm: ClassificationPageViewModel;
    index: number;
    suggestion: ClassificationSuggestion;
    noun: CategoryNoun;
}

function SuggestionCard({ vm, index, suggestion, noun }: SuggestionCardProps): ReactElement {
    const selected = vm.selectedSuggestionIndices.has(index);
    const fields = suggestion.fields;
    const isAdd = suggestion.kind === 'add';
    const showName = isAdd || fields.name !== undefined;
    const showDescription = isAdd || fields.description !== undefined;
    const showInstructions = isAdd || fields.instructions !== undefined;
    const showOutputType = isAdd || fields.outputType !== undefined;

    return (
        <Paper withBorder radius="md" p="sm">
            <Group align="flex-start" wrap="nowrap" gap="sm">
                <Checkbox mt={4} checked={selected} onChange={() => vm.toggleSuggestion(index)} aria-label={`Select suggestion ${index + 1}`} />
                <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
                    <Badge size="sm" variant="light" color={isAdd ? 'teal' : 'violet'} style={{ alignSelf: 'flex-start' }}>
                        {isAdd ? `New ${noun.singular.toLowerCase()}` : `Update — ${suggestion.targetName}`}
                    </Badge>

                    {showName && (
                        <TextInput
                            size="xs"
                            label="Name"
                            value={fields.name ?? ''}
                            onChange={(event) => vm.setSuggestionField(index, { name: event.currentTarget.value })}
                        />
                    )}
                    {showDescription && (
                        <Textarea
                            size="xs"
                            label="Description"
                            autosize
                            minRows={1}
                            maxRows={3}
                            value={fields.description ?? ''}
                            onChange={(event) => vm.setSuggestionField(index, { description: event.currentTarget.value })}
                        />
                    )}
                    {showInstructions && (
                        <Textarea
                            size="xs"
                            label="Instructions"
                            autosize
                            minRows={2}
                            maxRows={6}
                            value={fields.instructions ?? ''}
                            onChange={(event) => vm.setSuggestionField(index, { instructions: event.currentTarget.value })}
                        />
                    )}
                    {showOutputType && (
                        <Group gap="xs" align="flex-end" wrap="nowrap">
                            <Select
                                size="xs"
                                label="Output type"
                                w={180}
                                allowDeselect={false}
                                data={OUTPUT_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                                value={fields.outputType ?? 'scale'}
                                onChange={(value) => value && vm.setSuggestionOutputType(index, value as ClassificationOutputType)}
                            />
                            <OutputValue fields={fields} />
                        </Group>
                    )}
                    {showOutputType && (fields.outputType ?? 'scale') === 'category' && (
                        <TextInput
                            size="xs"
                            label="Categories (comma-separated)"
                            placeholder="e.g. pii_high_risk, financial_leak, none"
                            value={categoryOptionsText(fields)}
                            onChange={(event) => vm.setSuggestionField(index, { outputConfig: { options: parseOptions(event.currentTarget.value) } })}
                        />
                    )}

                    {suggestion.rationale.length > 0 && (
                        <Text size="xs" c="dimmed">
                            {suggestion.rationale}
                        </Text>
                    )}
                </Stack>
            </Group>
        </Paper>
    );
}

/** The concrete value a chosen output type resolves to (e.g. "1–10", "USD"). Category options have their own editor. */
function OutputValue({ fields }: { fields: ClassificationSuggestionFields }): ReactElement | null {
    const outputType = fields.outputType ?? 'scale';
    if (outputType === 'category') return null;
    const summary = describeOutputConfig(outputType, fields.outputConfig ?? {});
    if (summary.length === 0) return null;
    return (
        <Badge size="sm" variant="outline" color="gray" mb={4} style={{ flexShrink: 0 }}>
            {summary}
        </Badge>
    );
}

/** Comma-joined view of a category suggestion's option labels. */
function categoryOptionsText(fields: ClassificationSuggestionFields): string {
    const options = fields.outputConfig?.['options'];
    return Array.isArray(options) ? options.join(', ') : '';
}

/** Parses a comma-separated list into trimmed, non-empty option labels. */
function parseOptions(value: string): string[] {
    return value
        .split(',')
        .map((option) => option.trim())
        .filter((option) => option.length > 0);
}
